import React from "react";
import { Store } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CreateStoreFormData } from "./createStoreSchema";
import { MapboxAddressInput } from "./MapboxAddressInput";
import { useTranslation } from "react-i18next";

interface BasicInfoStepProps {
  form: UseFormReturn<CreateStoreFormData>;
}

export const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ form }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-lg font-medium">
        <Store className="h-5 w-5" />
        <h2>{t("basicInfoStep.basicInformation")}</h2>
      </div>
      <Separator />

      {/* Basic Store Information */}
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.storeName")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.storeNamePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="legalName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.legalBusinessName")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.legalBusinessNamePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Mapbox Address Input */}
      <MapboxAddressInput form={form} />

      {/* Address Details */}
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="street"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.street")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.streetPlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="streetNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.streetNumber")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.streetNumberPlaceholder")}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.city")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.cityPlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="zip"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.postalCode")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.postalCodePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="state"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.state")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.statePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="companyPhone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.companyPhone")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.companyPhonePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.storePhone")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("basicInfoStep.storePhonePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("basicInfoStep.email")}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder={t("basicInfoStep.emailPlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
